'use client';
import { useLocale } from "next-intl";
import Image from "next/image";
import HeroButtonDialog from "@/components/home-page/HeroButton";
import { useTranslations } from "next-intl";
import React, { useEffect, useRef, useMemo, useState } from 'react';

export default function Hero() {
  const t = useTranslations('tophero');
  const te = useTranslations('Hero');
  const l = useLocale();
  const effectInitialized = useRef(false);
  
  // Pixelation effect state and refs
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const animationRef = useRef<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: -1000, y: -1000 });
  
  const pixelSize = 45;
  const effectRadius = 200;

  // Memoize the book image path to avoid recalculation
  const bookImagePath = useMemo(() => {
    const imageMap: Record<string, string> = {
      ar: "/cover/بادل-أتقن-اللعبة.avif",
      de: "/cover/padel-beherrsche-das-spiel.avif",
      es: "/cover/padel-domina-el-juego.avif",
      fr: "/cover/padel-maitrise-le-jeu.avif",
      it: "/cover/padel-padroneggia-il-gioco.avif",
      nl: "/cover/padel-beheers-het-spel.avif",
      pt: "/cover/padel-domine-o-jogo.avif",
      ru: "/cover/падел-овладей-игрой.avif",
      sv: "/cover/padel-bemastra-spelet.avif",
    };
    return imageMap[l] || "/cover/padel-master-the-game.avif";
  }, [l]);

  // Pixelation effect
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video) return;
    
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    
    const resizeCanvas = () => {
      if (!containerRef.current) return;
      canvas.width = containerRef.current.offsetWidth;
      canvas.height = containerRef.current.offsetHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const render = () => {
      if (!video || video.readyState < 2) {
        animationRef.current = requestAnimationFrame(render);
        return;
      }
      
      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        if (mousePos.x >= 0 && mousePos.y >= 0) {
          const startX = Math.max(0, Math.floor(mousePos.x - effectRadius));
          const startY = Math.max(0, Math.floor(mousePos.y - effectRadius));
          const endX = Math.min(canvas.width, Math.ceil(mousePos.x + effectRadius));
          const endY = Math.min(canvas.height, Math.ceil(mousePos.y + effectRadius));
          
          const width = endX - startX;
          const height = endY - startY;
          
          if (width > 0 && height > 0) {
            const imageData = ctx.getImageData(startX, startY, width, height);
            const data = imageData.data;
            
            for (let y = 0; y < height; y += pixelSize) {
              for (let x = 0; x < width; x += pixelSize) {
                const blockCenterX = startX + x + pixelSize / 2;
                const blockCenterY = startY + y + pixelSize / 2;
                const distanceFromMouse = Math.sqrt(
                  Math.pow(blockCenterX - mousePos.x, 2) + 
                  Math.pow(blockCenterY - mousePos.y, 2)
                );
                
                if (distanceFromMouse <= effectRadius) {
                  let r = 0, g = 0, b = 0, a = 0;
                  let count = 0;
                  
                  for (let dy = 0; dy < pixelSize && y + dy < height; dy++) {
                    for (let dx = 0; dx < pixelSize && x + dx < width; dx++) {
                      const idx = ((y + dy) * width + (x + dx)) * 4;
                      r += data[idx];
                      g += data[idx + 1];
                      b += data[idx + 2];
                      a += data[idx + 3];
                      count++;
                    }
                  }
                  
                  r = Math.floor(r / count);
                  g = Math.floor(g / count);
                  b = Math.floor(b / count);
                  a = Math.floor(a / count);
                  
                  const fadeStrength = 1 - (distanceFromMouse / effectRadius);
                  
                  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
                  const blockWidth = Math.min(pixelSize, width - x);
                  const blockHeight = Math.min(pixelSize, height - y);
                  ctx.fillRect(startX + x, startY + y, blockWidth, blockHeight);
                  
                  if (fadeStrength > 0.5) {
                    ctx.strokeStyle = `rgba(0, 0, 0, ${0.1 * fadeStrength})`;
                    ctx.lineWidth = 1;
                    ctx.strokeRect(startX + x, startY + y, blockWidth, blockHeight);
                  }
                }
              }
            }
          }
        }
      } catch (e) {
        console.error('Canvas rendering error:', e);
      }
      
      animationRef.current = requestAnimationFrame(render);
    };
    
    const handleVideoReady = () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      render();
    };
    
    if (video.readyState >= 2) {
      render();
    } else {
      video.addEventListener('loadeddata', handleVideoReady);
    }
    
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      video.removeEventListener('loadeddata', handleVideoReady);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [mousePos]);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  
  const handleMouseLeave = () => {
    setMousePos({ x: -1000, y: -1000 });
  };

  return (
    <>
  
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 bg-bl px-4 lg:px-16 py-6">
        <div className="lg:col-span-3 flex justify-center items-center">
          <div>
            <h2 className="text-xl md:text-3xl lg:text-4xl  font-bold text-yel">
              {te('title')}
            </h2>
            <p className="text-white text-xl mt-1 font-bold">
              <span className="text-secondary bg-yel  px-2 font-bold">{te('Hurry')}</span> {te('t1')}
            </p>
            <p className="text-white mt-4 font-bold">
              {te('t3')}{" "}
              <span className="text-secondary bg-yel py-1 px-2 font-bold">{te('valued')}</span>
            </p>
            <p className="text-white text-sm sm:text-base font-bold">
              {te('t4')}
            </p>
          </div>
        </div>
        <div>
          <div className="h-60 w-auto relative">
            <Image
              src={bookImagePath}
              alt={te('padel-book-alt')}
              quality={85}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 20vw"
              style={{ objectFit: "contain" }}
              priority
            />
          </div>
        </div>
        <div className="flex justify-center items-center">
          <HeroButtonDialog />
        </div>
      </div>
    </>
  );
}